import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from '@/components/forge/Chip'
import { IconButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Mono, Num } from '@/components/forge/primitives'
import { formatDateTime, formatDecimal, formatInteger, formatSigned } from '@/lib/format'
import { cn } from '@/lib/cn'
import { IST_STAMP, IST_TICK } from './time'

/**
 * A sensor trace, with the controls that make a long one readable.
 *
 * One component for both modes. The anomaly window and the live feed are
 * different subjects off different endpoints, but once the readings are in hand
 * they are the same `{at, value}[]` and the same questions get asked of them —
 * how much of it am I looking at, which part, and can I have it bigger. A second
 * chart for the second mode would be two places for those answers to drift.
 *
 * ── The window is in READING time, not in index ─────────────────────────
 * `spanMs` selects a duration and `offset` slides it, both measured against the
 * stamps on the readings. Slicing by index instead would make "30 seconds" mean
 * a different amount of time per sensor, since nothing guarantees two tags are
 * sampled at the same rate — and on this screen two cards sit side by side
 * inviting exactly that comparison.
 */
export function TraceChart({
  readings,
  spans,
  spanMs,
  onSpanChange,
  band = null,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  live = false,
  onFullscreen,
  fullscreen = false,
  className,
}) {
  const { t } = useTranslation(['app', 'common'])

  /* How far back from the newest reading the visible window starts, in ms. 0 is
     "the end of the data", which is the live edge in live mode and the end of
     the event in history. */
  const [rawOffset, setOffset] = useState(0)

  const bounds = useMemo(() => {
    const stamps = readings
      .map((reading) => (reading.at ? Date.parse(reading.at) : NaN))
      .filter(Number.isFinite)
    if (!stamps.length) return null
    return { first: stamps[0], last: stamps[stamps.length - 1] }
  }, [readings])

  const total = bounds ? bounds.last - bounds.first : 0
  /* A span wider than the data shows all of it; there is nothing to pan over and
     the arrows below go quiet rather than scrolling an empty axis. */
  const span = spanMs && spanMs < total ? spanMs : total
  const maxOffset = Math.max(total - span, 0)

  /**
   * Clamped on READ, not corrected in an effect.
   *
   * The data grows under a live chart and shrinks when a span widens, so the
   * stored offset can fall outside the range at any moment. Deriving the usable
   * value here keeps the chart correct on the very render where that happens —
   * an effect would paint one frame with a window past the end of the data, and
   * would cost a second render to fix it.
   */
  const offset = Math.min(rawOffset, maxOffset)

  const visible = useMemo(() => {
    if (!bounds) return readings
    const end = bounds.last - offset
    const start = end - span
    return readings.filter((reading) => {
      const stamp = reading.at ? Date.parse(reading.at) : NaN
      if (!Number.isFinite(stamp)) return false
      return stamp >= start && stamp <= end
    })
  }, [readings, bounds, offset, span])

  /* One step is half a screen, which is the convention that keeps context: a
     full-width step leaves nothing shared between the before and the after, and
     the reader loses the shape they were following. */
  const step = Math.max(Math.round(span / 2), 1)
  const canGoBack = offset < maxOffset

  /**
   * Forward either pans or FETCHES, and the arrow does not say which.
   *
   * The readings arrive a page at a time now. At the right-hand edge of what is
   * loaded there is nothing left to pan to, and the next thing in that direction
   * is the next page — so the same control asks for it. Two controls, one to
   * move and one to load, would make the reader learn a distinction that only
   * exists because of how the data happens to be fetched.
   *
   * The pan is preferred when it is available: somebody scrubbing through loaded
   * data should not trigger a request halfway along.
   */
  const atLoadedEdge = offset === 0
  const canFetchMore = atLoadedEdge && hasMore && !loadingMore && Boolean(onLoadMore)
  const canGoForward = offset > 0 || canFetchMore

  const goForward = () => {
    if (offset > 0) {
      setOffset((value) => Math.max(value - step, 0))
      return
    }
    /* `offset` stays at 0, which is "the newest loaded reading". The arriving
       page extends the data to the right, so the view follows it forward
       without the reader having to press again. */
    onLoadMore?.()
  }

  /* `h-full` when expanded, so the whole chart is a flex column inside the stage
     and the PLOT takes whatever the controls and figures leave. A fixed plot
     height would have to guess at the viewport, and would either scroll or leave
     a band of empty canvas under the trace. */
  return (
    <div className={cn('flex flex-col gap-2.5', fullscreen && 'h-full', className)}>
      <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1.5">
        {spans?.length ? (
          <div className="flex flex-wrap gap-1">
            {spans.map((option) => (
              <Chip
                key={option.key}
                selected={spanMs === option.ms}
                onClick={() => {
                  onSpanChange(option.ms)
                  /* Back to the edge on a span change. Holding the offset would
                     land the reader somewhere arbitrary — the same number of
                     milliseconds back, which is a different part of a different
                     sized window. */
                  setOffset(0)
                }}
              >
                {option.key === 'full' && hasMore
                  ? /* "Full window" would be a claim this chart cannot make while
                       pages are still unfetched: it shows all that is LOADED,
                       which is a different and smaller thing. */
                    t('anomalies.analysis.ts.span.loaded')
                  : t(`anomalies.analysis.ts.span.${option.key}`)}
              </Chip>
            ))}
          </div>
        ) : null}

        <div className="flex-1" />

        {/* Shown when there is anywhere to go — somewhere to pan, or another page
            to fetch. A disabled pair on a chart that holds everything is clutter
            stating the obvious. */}
        {maxOffset > 0 || hasMore ? (
          <div className="flex items-center gap-1">
            <IconButton
              bare
              disabled={!canGoBack}
              label={t('anomalies.analysis.ts.panBack')}
              onClick={() => setOffset((value) => Math.min(value + step, maxOffset))}
            >
              <Icon name="right" size="sm" className="rotate-180" />
            </IconButton>
            <IconButton
              bare
              disabled={!canGoForward}
              /* Named for what the press will do. At the edge of loaded data the
                 arrow fetches rather than moves, and a label still promising to
                 pan would be describing the wrong action — to a screen reader,
                 the only description there is. */
              label={t(
                canFetchMore && offset === 0
                  ? 'anomalies.analysis.ts.loadMore'
                  : 'anomalies.analysis.ts.panForward',
              )}
              onClick={goForward}
            >
              <Icon
                name="right"
                size="sm"
                className={cn(loadingMore && 'animate-pulse text-forge')}
              />
            </IconButton>
          </div>
        ) : null}

        {onFullscreen ? (
          <IconButton
            bare
            label={t(fullscreen ? 'anomalies.analysis.ts.exitFullscreen' : 'anomalies.analysis.ts.fullscreen')}
            onClick={onFullscreen}
          >
            <Icon name={fullscreen ? 'minus' : 'layers'} size="sm" />
          </IconButton>
        ) : null}
      </div>

      {visible.length < 2 ? (
        <p
          className={cn(
            'm-0 py-10 text-center text-body-sm text-ink-dim',
            fullscreen && 'flex flex-1 items-center justify-center py-0',
          )}
        >
          {t(live ? 'anomalies.analysis.ts.awaitingLive' : 'anomalies.analysis.ts.noReadings')}
        </p>
      ) : (
        <Plot
          readings={visible}
          band={band}
          hasMore={hasMore}
          fullscreen={fullscreen}
          live={live}
        />
      )}
    </div>
  )
}

/* The drawing box in user units. The SVG scales to its container; these only set
   the coordinate space the path is computed in. */
const W = 320
const H = 110

/**
 * The readings, drawn.
 *
 * ── The area under the line is what makes two cards comparable ───────────
 * A bare polyline at this size reads as a squiggle. The fill under it gives the
 * trace a mass the eye compares across cards without reading either axis, which
 * is the whole job of a board like this. A flat opacity on one hue, not a
 * gradient — DESIGN.md forbids the second and the first earns nothing here.
 *
 * ── The extent is the trace's own, and it is labelled ────────────────────
 * The y-axis spans the VISIBLE readings' own min and max rather than a rounded
 * or shared scale, so a 2% wobble and a 400% spike both fill the card. That is
 * the right shape for reading one sensor and the wrong one for comparing two,
 * which is exactly why both numbers are printed beside it: the figures carry the
 * magnitude, the shape carries the event.
 *
 * Recomputed per visible slice rather than held across pans, deliberately. A
 * fixed scale would make most pans show a flat line near one edge; the labels
 * say what the axis is each time, so the scale changing is visible rather than
 * silent.
 *
 * There is no unit. `AnomalySensorTagOut` carries `value_type` and no unit — the
 * units live on the sensor, which this payload does not reach. Printing a bare
 * number is honest; guessing one from the sensor name would not be. Recorded in
 * BACKEND-GAPS.md.
 */
function Plot({ readings, band, hasMore, fullscreen, live }) {
  const { t } = useTranslation(['app', 'common'])

  const values = readings.map((reading) => reading.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1

  const x = (index) => (index / Math.max(values.length - 1, 1)) * W
  const y = (value) => H - ((value - min) / span) * (H - 6) - 3
  const line = values.map((value, index) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(' ')

  const first = readings[0]
  const last = readings[readings.length - 1]

  /**
   * The figures, all derived from the VISIBLE slice.
   *
   * ── Derived, never fetched, and nothing here is a judgement ────────────
   * Min, max, range, mean and change are arithmetic over the readings on screen.
   * They move when the span or the pan moves, which is correct: they describe
   * what is being looked at. What is deliberately absent is anything that would
   * read as an assessment — no baseline, no sigma, no threshold, no "normal".
   * The analysis states those in prose on the RCA view, computed server-side
   * against a baseline this screen does not have; recomputing them here from a
   * slice somebody happened to pan to would produce a second, different number
   * under the same name.
   *
   * ── `change` is last minus first, and it is signed ─────────────────────
   * Not a rate and not a trend: two endpoints, named as such. A gradient fitted
   * over a slice the reader chose would be a claim about direction that the slice
   * was not selected to support.
   */
  const stats = [
    { key: 'latest', value: last?.value },
    { key: 'min', value: min },
    { key: 'max', value: max },
    { key: 'range', value: max - min },
    { key: 'mean', value: values.reduce((total, value) => total + value, 0) / values.length },
    { key: 'change', value: (last?.value ?? 0) - (first?.value ?? 0), signed: true },
  ]

  const elapsed =
    first?.at && last?.at ? Date.parse(last.at) - Date.parse(first.at) : null

  /**
   * Which reading the pointer is over, in the expanded view.
   *
   * ── Index, not value, and the x mapping is why ─────────────────────────
   * The trace is plotted by POSITION in the array rather than by timestamp —
   * `x(index)` spreads the readings evenly — so the inverse is arithmetic on the
   * pointer's fraction across the box, with no search and no nearest-point
   * distance to compute. It also means the readout cannot land between two
   * readings and interpolate a value nobody measured.
   *
   * ── Hover only, and that is a real limit ──────────────────────────────
   * There is no keyboard path to a single reading, so this adds nothing for
   * somebody not using a pointer. It is an enhancement rather than the only way
   * to the numbers: min, max, latest, mean, range and change are all printed
   * below the chart, and the axis states both ends. A focusable per-point
   * affordance on a 400-point trace would be 400 tab stops, which is worse than
   * none; a value-at-time cursor driven by arrow keys is the right answer and is
   * a bigger piece of work than this.
   */
  const [hover, setHover] = useState(null)
  const hovered =
    hover != null && readings[hover] ? { index: hover, reading: readings[hover] } : null

  const onPointerMove = (event) => {
    const box = event.currentTarget.getBoundingClientRect()
    if (!box.width) return
    const ratio = (event.clientX - box.left) / box.width
    /* Clamped before rounding: a pointer one pixel outside the box on either
       edge should read the first or last point, not fall off the array. */
    const index = Math.round(Math.min(Math.max(ratio, 0), 1) * (readings.length - 1))
    setHover(index)
  }

  /**
   * The axis ticks.
   *
   * ── Counted off the size, because a tick is only useful if it is legible ──
   * Five each way expanded, three on a card. The card's plot is 112px tall: five
   * y labels at 10px leaves four pixels between them and the column reads as a
   * smudge rather than as a scale. The same five across a 26rem card's width put
   * three timestamps closer together than one timestamp is wide.
   *
   * ── The y ticks are EVEN DIVISIONS of the visible extent ────────────────
   * Not "nice" rounded numbers. A rounded scale would have to extend past the
   * data to land on them, and this axis is deliberately the trace's own min and
   * max — see the note above. So the divisions are arithmetic and the labels say
   * whatever those divisions are, which is also what makes the top and bottom
   * labels exactly the max and min the figures below repeat.
   *
   * A FLAT trace collapses to one label. `max === min` makes every division the
   * same number, and a column of five identical values is a scale that is lying
   * about having a range.
   */
  const yTickCount = fullscreen ? 5 : 3
  const yTicks =
    max === min
      ? [max]
      : Array.from(
          { length: yTickCount },
          (_, index) => max - (index * (max - min)) / (yTickCount - 1),
        )

  /**
   * The x ticks, as READINGS rather than as interpolated instants.
   *
   * Sampled by index and de-duplicated, so a trace with fewer points than ticks
   * labels each point once instead of printing the same stamp twice. Every label
   * is therefore a time something was actually measured.
   */
  const xTickCount = fullscreen ? 5 : 3
  const xTicks = [
    ...new Set(
      Array.from({ length: xTickCount }, (_, index) =>
        Math.round((index * (readings.length - 1)) / Math.max(xTickCount - 1, 1)),
      ),
    ),
  ].map((index) => readings[index])

  /* The gutter the y labels sit in, and the indent the x labels need to line up
     with the plot rather than with the labels beside it. */
  const gutter = fullscreen ? 'w-16' : 'w-12'

  /**
   * The detection band — `detected_at` to `last_detected_at`, over the trace.
   *
   * ── Positioned through the READINGS, not by time fraction ──────────────
   * The trace is drawn by index: point `i` sits at `i / (n-1)` across the box
   * regardless of when it was taken. Placing the band by its share of the
   * visible time span would be more precise in the abstract and WRONG here —
   * on unevenly sampled data the band would drift away from the features it is
   * supposed to be bracketing. So each edge resolves to the nearest reading and
   * takes that reading's x, which keeps it welded to the trace.
   *
   * ── Clamped to the plot, and the clamped edge draws no line ────────────
   * `last_detected_at` routinely falls AFTER `window_end` — six seconds after,
   * on ANM-288 — and the readings stop at the window. An edge outside the
   * visible range pulls the band to the border and omits its boundary line, so
   * the shading says "this continues past here" instead of a line claiming the
   * detector stopped firing exactly where the data runs out.
   *
   * Nothing is drawn when the band misses the visible slice entirely, which is
   * the ordinary case once somebody has panned away from it.
   */
  const detection = useMemo(() => {
    if (!band?.from || !band?.to || readings.length < 2) return null

    const from = Date.parse(band.from)
    const to = Date.parse(band.to)
    const stamps = readings.map((reading) => (reading.at ? Date.parse(reading.at) : NaN))
    const start = stamps.find(Number.isFinite)
    const end = [...stamps].reverse().find(Number.isFinite)
    if (![from, to, start, end].every(Number.isFinite)) return null
    if (to < start || from > end) return null

    const nearest = (time) => {
      let best = 0
      let bestGap = Infinity
      stamps.forEach((stamp, index) => {
        if (!Number.isFinite(stamp)) return
        const gap = Math.abs(stamp - time)
        if (gap < bestGap) {
          bestGap = gap
          best = index
        }
      })
      return best
    }

    return {
      fromX: from <= start ? 0 : (nearest(from) / (readings.length - 1)) * W,
      toX: to >= end ? W : (nearest(to) / (readings.length - 1)) * W,
      // Only an edge that is genuinely inside the data gets a line of its own.
      showFrom: from > start && from <= end,
      showTo: to >= start && to < end,
    }
  }, [band, readings])

  return (
    <>
      {/* `min-h-0` lets the plot give height back to the rows around it;
          `min-h-[12rem]` stops it giving back ALL of it. On a short viewport the
          trace would otherwise collapse to a line while the figures below stayed
          at full height, which inverts what the reader opened this for. */}
      <div
        className={cn(
          'flex items-stretch gap-2',
          fullscreen && 'min-h-[12rem] flex-1 basis-0',
        )}
      >
        {/* The y-axis, as HTML beside the plot rather than text inside it.
            The SVG is drawn with `preserveAspectRatio="none"` so that the trace
            fills whatever box it is given — which stretches any `<text>` in it
            horizontally by the same factor. Labels therefore live outside, and
            the gridlines they name live inside, where a line distorts harmlessly
            and `vectorEffect` keeps its weight. */}
        <div
          aria-hidden="true"
          className={cn(
            'flex h-full shrink-0 flex-col py-[3px] text-end',
            gutter,
            yTicks.length > 1 ? 'justify-between' : 'justify-center',
          )}
        >
          {yTicks.map((value, index) => (
            <Num key={index} className="block text-micro text-ink-mute">
              {formatReading(value)}
            </Num>
          ))}
        </div>

        {/* `relative`, because the readout rides ON the plot and has to be HTML:
            see `Readout`. The pointer handlers sit here rather than on the <svg>
            so the hit area is the whole plot box, including the gap above a
            trace that does not reach the top. */}
        <div
          className={cn('relative min-w-0 flex-1', fullscreen ? 'h-full' : 'h-28')}
          onPointerMove={fullscreen ? onPointerMove : undefined}
          onPointerLeave={fullscreen ? () => setHover(null) : undefined}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={t('anomalies.analysis.ts.traceLabel', { count: readings.length })}
            className="h-full w-full"
          >
            {/* One rule per labelled value, and one per labelled time. The ends
                are drawn at a lower opacity than the middles: they sit on the
                edge of the box, where a full-strength line reads as a border the
                trace is boxed in by rather than as a scale it is measured
                against. */}
            {yTicks.map((value, index) => (
              <line
                key={`y-${index}`}
                x1="0"
                x2={W}
                y1={y(value)}
                y2={y(value)}
                stroke="var(--hue-forge)"
                strokeOpacity={index === 0 || index === yTicks.length - 1 ? 0.12 : 0.18}
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {xTicks.map((reading, index) =>
              index === 0 || index === xTicks.length - 1 ? null : (
                <line
                  key={`x-${index}`}
                  x1={(index * W) / Math.max(xTicks.length - 1, 1)}
                  x2={(index * W) / Math.max(xTicks.length - 1, 1)}
                  y1="0"
                  y2={H}
                  stroke="var(--hue-forge)"
                  strokeOpacity="0.1"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              ),
            )}

            {/* ── The detection band, UNDER the trace ──────────────────────
                Drawn before the polygon and the polyline so the signal stays on
                top of its own annotation. A band painted over the trace would
                tint the one thing on the chart that has to be read exactly.

                `--hue-red`, by request, and reached as the TOKEN rather than as
                a value — which is the part that matters beyond the Single-Palette
                Rule: colour-blind mode redefines `--hue-red` to Wong vermillion
                (#D55E00), so the band follows the reader's mode without a second
                code path. A literal would have stayed trip-red for everyone.

                Dotted on both edges. They were solid and dashed, to tell the
                first detection from the last; the two are named in place when the
                chart is expanded and by the legend below when it is not, so the
                distinction is carried by words rather than by a dash pattern
                nobody was given a key to.

                DESIGN.md's rule against tinted regions is about CARDS — a shaded
                interval on a plot is an axis annotation, which is the one place a
                region genuinely carries meaning that an edge cannot. */}
            {detection ? (
              <>
                {detection.toX > detection.fromX ? (
                  <rect
                    x={detection.fromX}
                    y="0"
                    width={detection.toX - detection.fromX}
                    height={H}
                    fill="var(--hue-red)"
                    fillOpacity="0.12"
                  />
                ) : null}
                {detection.showFrom ? (
                  <line
                    x1={detection.fromX}
                    x2={detection.fromX}
                    y1="0"
                    y2={H}
                    stroke="var(--hue-red)"
                    strokeOpacity="0.9"
                    strokeWidth="1.5"
                    strokeDasharray="2 3"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
                {detection.showTo ? (
                  <line
                    x1={detection.toX}
                    x2={detection.toX}
                    y1="0"
                    y2={H}
                    stroke="var(--hue-red)"
                    strokeOpacity="0.9"
                    strokeWidth="1.5"
                    strokeDasharray="2 3"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
              </>
            ) : null}

            {/* Fill first, so the stroke sits on top of its own edge rather than
                being half-covered by it. */}
            <polygon
              points={`${line} ${W},${H} 0,${H}`}
              fill="var(--hue-forge)"
              fillOpacity="0.12"
            />
            <polyline
              points={line}
              fill="none"
              stroke="var(--hue-forge)"
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />

            {/* The crosshair is the one marker that CAN live in the SVG: a
                vertical line has no width to distort, and `vectorEffect` keeps
                its weight through the non-uniform scale. */}
            {hovered ? (
              <line
                x1={x(hovered.index)}
                x2={x(hovered.index)}
                y1="0"
                y2={H}
                stroke="var(--hue-forge)"
                strokeOpacity="0.45"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
          </svg>

          {/* The two edges, named. Expanded only: at card height these would sit
              on top of the trace, and the band's own shape already says where it
              is. Solid line for the first detection, dashed for the last — the
              same pair the legend below repeats, so the chart is readable without
              it and the legend is not the only key. */}
          {fullscreen && detection ? (
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
              {detection.showFrom ? (
                <BandLabel
                  leftPct={(detection.fromX / W) * 100}
                  label={t('anomalies.analysis.ts.fact.detected')}
                />
              ) : null}
              {detection.showTo ? (
                <BandLabel
                  leftPct={(detection.toX / W) * 100}
                  label={t('anomalies.analysis.ts.fact.lastDetected')}
                />
              ) : null}
            </div>
          ) : null}

          {hovered ? (
            <Readout
              reading={hovered.reading}
              leftPct={(x(hovered.index) / W) * 100}
              topPct={(y(hovered.reading.value) / H) * 100}
            />
          ) : null}
        </div>
      </div>

      {/* The x axis, offset by an empty gutter of the same width as the y labels
          so the first stamp sits under the START of the trace rather than under
          the column beside it.

          Times only, no date: all four window stamps in the header above carry
          the date, and the span these cover is seconds to minutes. A date on
          every tick would be the same date five times and would not fit. */}
      <div className="flex shrink-0 gap-2">
        <span aria-hidden="true" className={cn('shrink-0', gutter)} />
        <div className="flex min-w-0 flex-1 justify-between gap-2">
          {xTicks.map((reading, index) => (
            <Mono
              key={index}
              className={cn(
                'text-micro whitespace-nowrap text-ink-mute',
                /* The middles are centred on their rule; the ends align to the
                   edges of the plot so neither hangs outside it. */
                index === 0 && 'text-start',
                index === xTicks.length - 1 && 'text-end',
              )}
            >
              {reading?.at ? formatDateTime(reading.at, IST_TICK) : '—'}
            </Mono>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1">
        {/* Said plainly, because the chart otherwise looks like the whole window
            and is not. The forward arrow is how the rest arrives. */}
        {hasMore ? (
          <span className="text-micro text-ink-mute italic">
            {t('anomalies.analysis.ts.partial')}
          </span>
        ) : null}

        {/* The band, named. The expanded view labels its two edges in place; a
            card has no room for that, so this one line is the only thing saying
            what the shading is — and a shaded region nobody can name is just a
            chart with a stain on it. */}
        {detection ? (
          <span className="flex items-center gap-1.5 text-micro text-ink-mute">
            <span
              aria-hidden="true"
              className="h-2.5 w-4 shrink-0 rounded-xs"
              style={{ background: 'var(--hue-red)', opacity: 0.45 }}
            />
            {t('anomalies.analysis.ts.detectionBand')}
          </span>
        ) : null}

        {fullscreen && elapsed != null ? (
          <Mono className="text-micro text-ink-mute">{formatElapsed(t, elapsed)}</Mono>
        ) : null}
      </div>

      {fullscreen ? (
        <div className="flex shrink-0 flex-col gap-2.5">
          {/* The current value, given the size it is read at. In live mode it is
              the number somebody is watching the chart FOR, and in the window it
              is where the trace ended — either way it is the one figure that
              answers a question on its own, so it is not left as a sixth cell in
              a row of equals. */}
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-hairline pt-3">
            <span className="text-micro font-semibold tracking-wide text-ink-mute uppercase">
              {t('anomalies.analysis.ts.latest')}
            </span>
            <Num className="text-display font-semibold text-ink">
              {last?.value == null ? '—' : formatReading(last.value)}
            </Num>
            {/* Stated plainly rather than left for somebody to wonder about.
                `AnomalySensorTagOut` carries no unit; see BACKEND-GAPS.md. */}
            <span className="text-body-sm text-ink-mute">
              {t('anomalies.analysis.ts.noUnit')}
            </span>
          </div>

          <dl className="m-0 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(7rem,1fr))]">
            {stats
              .filter((stat) => stat.key !== 'latest')
              .map((stat) => (
                <Figure
                  key={stat.key}
                  label={t(`anomalies.analysis.ts.${stat.key}`)}
                  value={stat.value}
                  signed={stat.signed}
                />
              ))}
            <Figure
              label={t('anomalies.analysis.ts.points')}
              value={readings.length}
              integer
            />
          </dl>
        </div>
      ) : (
        <dl className="m-0 grid grid-cols-4 gap-2">
          <Figure label={t('anomalies.analysis.ts.min')} value={min} />
          <Figure label={t('anomalies.analysis.ts.max')} value={max} />
          <Figure label={t('anomalies.analysis.ts.latest')} value={last?.value} />
          <Figure label={t('anomalies.analysis.ts.points')} value={readings.length} integer />
        </dl>
      )}

      {live ? (
        <p className="m-0 text-micro text-ink-mute italic">{t('anomalies.analysis.ts.liveNote')}</p>
      ) : null}
    </>
  )
}

/**
 * One edge of the detection band, named.
 *
 * HTML over the SVG for the same reason the readout is: the plot is scaled
 * non-uniformly, so `<text>` inside it comes out stretched.
 *
 * It sits at the TOP of the plot, where a trace is least likely to be — the
 * y-axis is the visible extent, so the line only touches the top at its single
 * highest point. And it flips side at the right edge so a label near the end of
 * the window does not hang outside the box.
 */
function BandLabel({ leftPct, label }) {
  return (
    <span
      className={cn(
        /* On the opaque surface rather than on the band's own hue: there is no
           `tint-context` utility and inventing one would be a palette change for
           two labels. A bordered chip reads cleanly over the shading, and the
           hue goes on the TEXT, which is where it names something. */
        'absolute top-1 rounded-xs border border-hairline bg-overlay px-1.5 py-0.5 text-micro font-semibold whitespace-nowrap',
        leftPct > 70 ? '-translate-x-full' : '',
      )}
      style={{
        left: `${leftPct}%`,
        marginInlineStart: leftPct > 70 ? '-0.25rem' : '0.25rem',
        color: 'var(--hue-red)',
      }}
    >
      {label}
    </span>
  )
}

/**
 * The value and the time under the pointer.
 *
 * ── HTML over the SVG, not inside it ───────────────────────────────────
 * The plot is drawn with `preserveAspectRatio="none"`, which scales x and y by
 * different factors — so `<text>` comes out stretched and a `<circle>` comes out
 * an ellipse. Both are positioned here as absolutely placed HTML instead, in
 * percentages of the same box the SVG fills, which puts them exactly on the
 * point while leaving the type and the dot the shape they were drawn as.
 *
 * ── It flips at the edges rather than being clipped ────────────────────
 * Centred on the point for most of the trace; pinned to the left edge in the
 * first tenth and to the right in the last, because a centred label at x=0 hangs
 * half of itself outside the plot — and the first and last readings are exactly
 * the ones somebody checks.
 *
 * `pointer-events-none` throughout: the readout follows the pointer, and a label
 * that could intercept its own hover would flicker as it caught up.
 */
function Readout({ reading, leftPct, topPct }) {
  /* Three zones rather than a clamp, so the label never sits half outside the
     plot and never covers the point it is naming. */
  const align =
    leftPct < 12 ? 'start' : leftPct > 88 ? 'end' : 'center'

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {/* The point itself. Round, because it is HTML. */}
      <span
        className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-overlay bg-forge"
        style={{ left: `${leftPct}%`, top: `${topPct}%` }}
      />

      <div
        className={cn(
          /* `bg-overlay`, not `bg-card`: a card is 6% white in dark and the trace
             reads straight through a label naming one of its own points. */
          'absolute flex flex-col gap-0.5 rounded-sm border border-hairline bg-overlay px-2.5 py-1.5 whitespace-nowrap shadow-lift',
          align === 'center' && '-translate-x-1/2',
          align === 'end' && '-translate-x-full',
        )}
        style={{
          left: `${leftPct}%`,
          /* Above the point, or below it when the point is near the top — the
             label must not leave the plot box in either direction. */
          top: topPct < 24 ? `calc(${topPct}% + 1rem)` : undefined,
          bottom: topPct < 24 ? undefined : `calc(${100 - topPct}% + 1rem)`,
        }}
      >
        {/* ── Both lines carry full ink, where the stamp was muted ────────
            `text-ink-mute` is 40% in light and 38% in dark, and it is tuned for
            captions sitting STILL on a page. This label is read in the half
            second a pointer rests on a point, over a trace, and moves as soon as
            the pointer does — it is the worst place in the product for the
            quietest ink in it.

            Full ink on both lines rather than one step up to `ink-dim`: the two
            values are one answer, not a value with a footnote. The hierarchy
            between them is carried by size and weight — 13px semibold against a
            10px mono — which is the device the rest of the screen uses, and it
            survives the contrast being equal.

            This is a CALL-SITE choice between existing tokens, not a change to
            the palette: `--ink-dim` and `--ink-mute` keep the prototype's
            opacities everywhere else. See DESIGN.md correction 18. */}
        <Num className="text-body font-semibold text-ink">{formatReading(reading.value)}</Num>
        <Mono className="text-micro font-medium text-ink">
          {reading.at ? formatDateTime(reading.at, READOUT_STAMP) : '—'}
        </Mono>
      </div>
    </div>
  )
}

/**
 * The readout's clock, which carries SECONDS where the rest of the screen does not.
 *
 * `IST_STAMP` is `timeStyle: 'short'` — hours and minutes. That is right for the
 * window's two ends and useless here: a 54-second event has every reading inside
 * one minute, so every point would show the same time and the readout would look
 * stuck. Same zone, same 12-hour clock, one more field.
 */
const READOUT_STAMP = { ...IST_STAMP, timeStyle: 'medium' }

/** One figure under the trace. Tabular, so a row of them lines up on the digits. */
function Figure({ label, value, integer = false, signed = false }) {
  const text =
    value == null
      ? '—'
      : integer
        ? formatInteger(value)
        : signed
          ? formatSigned(value, 3)
          : formatReading(value)

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="truncate text-micro font-semibold tracking-wide text-ink-mute uppercase">
        {label}
      </dt>
      <dd className="m-0">
        <Num className="text-body-sm font-medium text-ink">{text}</Num>
      </dd>
    </div>
  )
}

/**
 * How long the visible slice covers.
 *
 * Two keys rather than one format string, because a duration under a minute and
 * one over it are different sentences in most languages and a single
 * "{m}m {s}s" would print "0m 45s" for the common case.
 */
function formatElapsed(t, ms) {
  const seconds = ms / 1000
  if (seconds < 60) {
    return t('anomalies.analysis.ts.elapsedSeconds', { value: formatDecimal(seconds, 1) })
  }
  return t('anomalies.analysis.ts.elapsedMinutes', {
    minutes: Math.floor(seconds / 60),
    seconds: formatDecimal(seconds % 60, 0),
  })
}

/**
 * A reading, printed.
 *
 * Three decimals, which is what the payload carries — 37.423, 2.46. Not
 * `Measure`: that takes a `{value, unit}` pair and there is no unit on this
 * record, so it would print a number with a trailing nothing.
 */
const formatReading = (value) => formatDecimal(value, 3)

/**
 * The chart, while it is still being fetched.
 *
 * ── Shaped like the chart, not like a list ──────────────────────────────
 * `SkeletonRows` was standing in here, and it draws stacked CARD-shaped blocks
 * with two pills and two text lines each — the shape of a logbook list, not of a
 * plot. Three of them are also taller than the chart they stand in for, so the
 * card grew while it loaded and collapsed when it finished, which on a grid of
 * four reflows the whole board twice.
 *
 * Every block here matches a real element one for one — the span chips, the plot
 * box at its exact height, the two axis stamps, the four figures — so nothing
 * moves when the readings land.
 *
 * ── It does not draw a fake trace ───────────────────────────────────────
 * The obvious flourish is a ghost waveform under the pulse. It is wrong here:
 * this screen's whole argument is that what you are looking at came off a
 * sensor, and a skeleton that resembles a signal is a shape somebody can read a
 * trend off for the second before it is replaced. A plain block cannot be
 * mistaken for a measurement.
 */
export function TraceSkeleton({ spans = 5, fullscreen = false, className }) {
  /* `common:` explicitly. This file's other components load `['app', 'common']`
     and the bare `loading` key lives in the second — `States.jsx` reaches it
     through the default namespace, which this file does not have. */
  const { t } = useTranslation(['app', 'common'])

  return (
    <div
      className={cn('flex flex-col gap-2.5', className)}
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{t('common:loading')}</span>

      {/* The span chips. Same count the loaded chart will show, so the row does
          not change width underneath the reader's cursor. */}
      <div className="flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: spans }, (_, index) => (
          <div
            key={index}
            className="h-[27px] animate-pulse rounded-sm bg-sunken"
            /* Widths follow the labels they stand for — "Full window" is wider
               than "5s" — because a row of identical pills reads as a progress
               bar rather than as controls. */
            style={{ width: index === 0 ? 86 : 44 }}
          />
        ))}
      </div>

      {/* The plot box and its y gutter, at the exact widths and height they
          replace — so the trace does not shift sideways when the labels appear. */}
      <div
        aria-hidden="true"
        className={cn('flex gap-2', fullscreen ? 'min-h-[12rem] flex-1 basis-0' : '')}
      >
        <div className={cn('shrink-0', fullscreen ? 'w-16' : 'w-12')} />
        <div
          className={cn(
            'min-w-0 flex-1 animate-pulse rounded-sm bg-sunken',
            fullscreen ? 'h-full' : 'h-28',
          )}
        />
      </div>

      <div className="flex gap-2" aria-hidden="true">
        <span className={cn('shrink-0', fullscreen ? 'w-16' : 'w-12')} />
        <div className="flex min-w-0 flex-1 justify-between gap-2">
          {Array.from({ length: fullscreen ? 5 : 3 }, (_, index) => (
            <div key={index} className="h-3 w-16 animate-pulse rounded-xs bg-sunken" />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex flex-col gap-1">
            <div className="h-2.5 w-12 animate-pulse rounded-xs bg-sunken" />
            <div className="h-4 w-16 animate-pulse rounded-xs bg-sunken" />
          </div>
        ))}
      </div>
    </div>
  )
}
